import { CONTENT_WIDTH, bottomMarginItemGlobal } from "../../utils/pdfConstants";
import { fetchMapImage } from "../../../api/api";

/**
 * Extract main building coordinates from rakennusData or mergedRakennusData
 * @param {object} rakennusData
 * @param {object} mergedRakennusData
 * @returns {Array<{lat:number,lng:number}>} coordinates of main buildings
 */
export function getMainBuildingCoordinates(rakennusData, mergedRakennusData, markerOverrides = {}) {
  const sourceData = mergedRakennusData || rakennusData;
  const buildings = sourceData?.rakennukset_fulls || [];

  return buildings
    .filter(b => b.isMainBuilding && b.sijainti?.coordinates)
    .map((b, idx) => {
      const ov = markerOverrides[idx];
      return ov
        ? { lat: ov.lat, lng: ov.lng }
        : { lat: b.sijainti.coordinates[1], lng: b.sijainti.coordinates[0] };
    });
}

/**
 * Render map image with all main buildings as markers
 */
export async function renderMapImage({
  content,
  rakennusData,
  mergedRakennusData,
  zoom = 16,
  width = 942,
  height = 624,
  blockData,
  contentWidth = CONTENT_WIDTH,
}) {
  const markerOverrides = blockData?.markerOverrides ?? {};
  const mainBuildings = getMainBuildingCoordinates(rakennusData, mergedRakennusData, markerOverrides);

  const drawnMarkers = (blockData?.drawnItemsGeoJSON?.features ?? [])
    .filter(f => f.geometry?.type === 'Point')
    .map(f => ({ lat: f.geometry.coordinates[1], lng: f.geometry.coordinates[0] }));

  const allMarkers = [...mainBuildings, ...drawnMarkers];

  if (!mainBuildings.length) {
    content.push({
      text: "Ei karttakuvadataa.",
      style: "paragraph",
      margin: [0, 5, 0, 10],
    });
    return;
  }

  const center = mainBuildings[0];

  try {
    const base64 = await fetchMapImage({
      lat: center.lat,
      lng: center.lng,
      zoom,
      width,
      height,
      markers: allMarkers,
      layer: blockData?.mapLayer ?? 'taustakartta',
      overlay: null,
      _cb: Date.now() // force fresh request
    });

    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = base64;
    });

    // Target aspect ratio for PDF
    const targetRatio = 16 / 9;
    const imgMaxWidth = contentWidth / 1.25;

    let cropWidth = img.width;
    let cropHeight = img.height;

    if (img.width / img.height > targetRatio) {
      cropWidth = img.height * targetRatio;
    } else {
      cropHeight = img.width / targetRatio;
    }

    const centerX = img.width / 2;
    const centerY = img.height / 2;

    const cropX = Math.max(0, Math.round(centerX - cropWidth / 2));
    const cropY = Math.max(0, Math.round(centerY - cropHeight / 2));

    const canvas = document.createElement("canvas");
    canvas.width = cropWidth;
    canvas.height = cropHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, cropX, cropY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);

    const croppedBase64 = canvas.toDataURL();

    const imgWidth = imgMaxWidth;
    const imgHeight = Math.round(imgWidth / targetRatio);

    content.push({
      image: croppedBase64,
      width: imgWidth,
      height: imgHeight,
      alignment: "center",
      margin: [0, 0, 0, bottomMarginItemGlobal],
      unbreakable: true,
    });
  } catch (err) {
    console.error("renderMapImage error:", err);
    content.push({
      text: "Karttakuvan lataaminen epäonnistui.",
      style: "paragraph",
      margin: [0, 5, 0, 10],
    });
  }
}